import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { addToDraft, parseDraft, quantityInDraft, type DraftItem } from './orderDraft';

/**
 * The customer's order in progress, kept in the browser tab (sessionStorage) under their account id so
 * it survives moving between the catalog and the create screen, and never shows to another user of the
 * same browser. Storage can be missing or throw (private window): then it lives in memory only.
 */

const PREFIX = 'lombok.order-cart.v1:';
const EMPTY: readonly DraftItem[] = [];

const cache = new Map<string, readonly DraftItem[]>();
const listeners = new Set<() => void>();

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, items: readonly DraftItem[]): void {
  try {
    if (items.length === 0) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Memory-only: the cache below still holds it for this page load.
  }
}

/** Same array reference until the content changes, as useSyncExternalStore requires. */
function read(key: string): readonly DraftItem[] {
  let items = cache.get(key);
  if (!items) {
    items = parseDraft(storageGet(key));
    cache.set(key, items);
  }
  return items;
}

function write(key: string, items: readonly DraftItem[]): void {
  cache.set(key, items.length === 0 ? EMPTY : items);
  storageSet(key, items);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export interface OrderCart {
  items: readonly DraftItem[];
  /** Number of lines (distinct SKUs). */
  count: number;
  quantityOf: (skuId: string) => number;
  add: (item: DraftItem) => void;
  /** Replace the whole order (used when repeating a past order or editing on the create screen). */
  replace: (items: readonly DraftItem[]) => void;
  clear: () => void;
}

/** `ownerId` is the customer's account id; without one the cart is always empty and does nothing. */
export function useOrderCart(ownerId: string | undefined): OrderCart {
  const key = ownerId ? PREFIX + ownerId : undefined;
  const items = useSyncExternalStore(
    subscribe,
    () => (key ? read(key) : EMPTY),
    () => EMPTY,
  );

  const add = useCallback(
    (item: DraftItem) => {
      if (key) write(key, addToDraft(read(key), item));
    },
    [key],
  );
  const replace = useCallback(
    (next: readonly DraftItem[]) => {
      if (key) write(key, next);
    },
    [key],
  );
  const clear = useCallback(() => {
    if (key) write(key, EMPTY);
  }, [key]);

  return useMemo(
    () => ({ items, count: items.length, quantityOf: (skuId: string) => quantityInDraft(items, skuId), add, replace, clear }),
    [items, add, replace, clear],
  );
}
